import { useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, Check, ShieldCheck, Gem, Sparkles, Headphones } from "lucide-react";
import { IMAGES, TRUST_POINTS } from "../../data/product";
import { useBag } from "../../context/BagContext";
import { trackAddToCart } from "../../lib/metaPixel";
import { JUDGEME_REVIEWS_VISIBLE } from "../../lib/judgeme";

const KEY_POINTS = [
  "Leaves hair feeling softer & smoother",
  "Lightweight moisturizing care",
  "Enhances natural-looking shine",
  "Leave-in formula — no rinsing",
  "Designed for effortless everyday hair care",
];

const trustIcons = [ShieldCheck, Gem, Sparkles, Headphones];

// Animation configs hoisted out of render (stable references).
const IMAGE_REVEAL = {
  initial: { opacity: 0, scale: 0.98 },
  whileInView: { opacity: 1, scale: 1 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};
const TRUST_FADE_UP = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
};
const trustTransition = (delay = 0) => ({ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] });

const PurchasePanel = ({ qty, setQty, price, money, onAdd }) => (
  <div className="mt-8 p-6 sm:p-8 bg-[#F8F4EC] border border-[rgba(58,42,37,0.08)] rounded-2xl" data-testid="purchase-panel">
    <div className="flex items-baseline justify-between">
      <p className="font-serif-display text-4xl text-[#3A2A25]" data-testid="product-price">
        {money(price)}
      </p>
      <p className="text-xs font-light text-[#9A8D85]">incl. 200 ml / 6.8 fl oz</p>
    </div>

    <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
      <div className="flex items-center justify-between sm:justify-start border border-[rgba(58,42,37,0.2)] bg-[#FFFDF9] rounded-full px-2">
        <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 text-[#756A64] hover:text-[#3A2A25] transition-colors duration-300" data-testid="qty-decrease-button">
          <Minus size={16} />
        </button>
        <span className="w-10 text-center text-sm font-medium" data-testid="qty-value">{qty}</span>
        <button onClick={() => setQty((q) => q + 1)} className="p-3 text-[#756A64] hover:text-[#3A2A25] transition-colors duration-300" data-testid="qty-increase-button">
          <Plus size={16} />
        </button>
      </div>
      <button
        onClick={onAdd}
        className="flex-1 px-8 py-4 bg-[#3A2A25] text-white text-xs font-semibold uppercase tracking-[0.2em] rounded-full hover:bg-[#55423A] hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(58,42,37,0.25)] transition-[background-color,transform,box-shadow] duration-300"
        data-testid="add-to-bag-button"
      >
        Add to Bag — {money(price * qty)}
      </button>
    </div>
  </div>
);

const TrustSection = () => (
  <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="trust-section">
    {TRUST_POINTS.map((t, i) => {
      const Icon = trustIcons[i];
      return (
        <motion.div
          key={t.title}
          {...TRUST_FADE_UP}
          transition={trustTransition(i * 0.08)}
          className="flex items-start gap-4 p-5 rounded-xl bg-[#F8F4EC] border border-[rgba(58,42,37,0.06)]"
          data-testid={`trust-point-${i}`}
        >
          <Icon size={20} className="text-[#C9A45C] mt-0.5 shrink-0" strokeWidth={1.5} />
          <div>
            <p className="text-sm font-medium text-[#2B1F1B]">{t.title}</p>
            <p className="text-xs font-light text-[#9A8D85] mt-1">{t.text}</p>
          </div>
        </motion.div>
      );
    })}
  </div>
);

export const Showcase = ({ product }) => {
  const [qty, setQty] = useState(1);
  const { addToBag } = useBag();
  const price = product?.price ?? 29.99;
  const currency = product?.currency ?? "USD";
  const money = (amount) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);

  return (
    <section id="shop" className="bg-[#FFFDF9] border-y border-[rgba(58,42,37,0.08)] scroll-mt-24" data-testid="product-showcase">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <motion.div
            {...IMAGE_REVEAL}
            className="rounded-2xl overflow-hidden bg-[#EDE3D6] lg:sticky lg:top-24"
          >
            <img src={IMAGES.packshot} alt="CELUSTA Leave-In Conditioner Spray 200 ml" loading="lazy" className="w-full object-contain max-h-[640px] p-6 sm:p-10" data-testid="showcase-image" />
          </motion.div>

          <div>
            <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#C9A45C] mb-4">The Product</p>
            <h2 className="font-serif-display font-medium text-4xl sm:text-5xl tracking-tight text-[#2B1F1B] leading-tight" data-testid="product-name">
              CELUSTA Leave-In Conditioner
            </h2>
            {JUDGEME_REVIEWS_VISIBLE && (
              <div className="mt-3 jdgm-widget jdgm-preview-badge" data-id="15964123070794" data-testid="judgeme-preview-badge"></div>
            )}
            <div className="mt-4 flex items-center gap-3">
              <span className="px-3 py-1 rounded-full bg-[#EDE3D6] text-[10px] font-mono-accent uppercase tracking-[0.15em] text-[#C9A45C]" data-testid="product-volume">200 ml</span>
              <span className="px-3 py-1 rounded-full bg-[#EDE3D6] text-[10px] font-mono-accent uppercase tracking-[0.15em] text-[#C9A45C]">Leave-in spray</span>
            </div>

            <PurchasePanel qty={qty} setQty={setQty} price={price} money={money} onAdd={() => { addToBag(qty); trackAddToCart(product, qty); }} />

            <ul className="mt-8 space-y-3" data-testid="product-key-benefits">
              {KEY_POINTS.map((point) => (
                <li key={point} className="flex items-start gap-3 text-sm font-light text-[#756A64]">
                  <Check size={16} className="text-[#C9A45C] mt-0.5 shrink-0" strokeWidth={2} />
                  {point}
                </li>
              ))}
            </ul>

            <div className="mt-10 rounded-2xl overflow-hidden border border-[rgba(58,42,37,0.08)]">
              <img
                src="/images/benefits-graphic.webp"
                alt="CELUSTA Conditioner Spray — protects your hair gently"
                loading="lazy"
                className="w-full h-auto"
                data-testid="benefits-graphic"
              />
            </div>

            <div className="mt-10 grid grid-cols-2 gap-x-8 gap-y-4 border-t border-[rgba(58,42,37,0.1)] pt-8" data-testid="product-specs">
              {(product?.specs || []).slice(0, 6).map((spec) => (
                <div key={spec.label}>
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.15em] text-[#9A8D85]">{spec.label}</p>
                  <p className="text-sm text-[#2B1F1B] font-light mt-0.5">{spec.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <TrustSection />
      </div>
    </section>
  );
};
